import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, PackageMinus } from "lucide-react";
import { fabricGroups, ordinal } from "@/lib/cutTickets";

const low = (s) => (s || "").trim().toLowerCase();

const guess = (mats, fabric, color, fused) => {
  const pool = fused ? mats.filter((m) => m.fusible || m.fused > 0) : mats;
  const same = pool.filter((m) => low(m.name) === low(fabric) || low(m.fabric_type) === low(fabric));
  return (same.find((m) => low(m.color) === low(color)) || same[0])?.id || "";
};

const initialLines = (t, mats) => {
  const pieces = t.rows.reduce((s, r) => s + r.quantity, 0) || 1;
  return fabricGroups(t.markers).flatMap((g) => t.rows.map((r) => ({
    fabric: g.label, fused: g.fused, color: r.color || "", meters: String(Math.round((g.meters * r.quantity / pieces) * 100) / 100), material_id: guess(mats, g.fabric, r.color, g.fused),
  })));
};

const stockOf = (m, fused) => {
  const f = Math.min(m.fused || 0, m.quantity || 0);
  return fused ? f : (m.quantity || 0) - f;
};

const LineRow = ({ l, i, mats, onChange }) => (
  <div className="grid grid-cols-[1fr_110px] items-center gap-2 sm:grid-cols-[160px_1fr_110px]" data-testid={`discharge-line-${i}`}>
    <p className="col-span-2 text-sm sm:col-span-1">{l.fabric}<span className="block text-xs text-muted-foreground">colore {l.color || "-"}</span></p>
    <Select value={l.material_id} onValueChange={(v) => onChange({ material_id: v })}>
      <SelectTrigger className="h-9" data-testid={`discharge-line-${i}-material`}><SelectValue placeholder="Scegli il tessuto del magazzino" /></SelectTrigger>
      <SelectContent>{(l.fused ? mats.filter((m) => m.fusible || m.fused > 0) : mats).map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {m.color || "senza colore"}{m.supplier ? ` · ${m.supplier}` : ""} — {stockOf(m, l.fused)} m {l.fused ? "adesivati" : "normali"}</SelectItem>)}</SelectContent>
    </Select>
    <Input type="number" min="0" step="0.01" value={l.meters} onChange={(e) => onChange({ meters: e.target.value })} className="h-9 text-right" data-testid={`discharge-line-${i}-meters`} />
  </div>
);

export const DischargeDialog = ({ t, onClose, onDone }) => {
  const qc = useQueryClient();
  const { data: all = [], isLoading } = useQuery({ queryKey: ["materials"], queryFn: async () => (await api.get("/materials")).data });
  const mats = useMemo(() => all.filter((m) => m.type === "tessuto"), [all]);
  const [lines, setLines] = useState(null);
  const shown = lines || (isLoading ? [] : initialLines(t, mats));
  const upd = (i, patch) => setLines(shown.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const go = useMutation({
    mutationFn: async () => (await api.post(`/cut-tickets/${t.id}/discharge`, {
      lines: shown.filter((l) => Number(l.meters) > 0).map((l) => ({ material_id: l.material_id, meters: Number(l.meters), color: l.color, fused: !!l.fused })),
    })).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["materials"] }); qc.invalidateQueries({ queryKey: ["cut-tickets"] }); toast.success("Tessuto scaricato dal magazzino"); onDone(d); },
    onError: (e) => toast.error(e.response?.data?.detail || "Scarico non riuscito"),
  });
  const missing = shown.some((l) => Number(l.meters) > 0 && !l.material_id);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto [&>*]:min-w-0" data-testid="discharge-dialog">
        <DialogHeader><DialogTitle>Scarica tessuto · {ordinal(t.number)} {t.sheet_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">I metri dei grafici sono divisi tra i colori in base ai capi di ogni colore: correggili se serve e scegli da quale tessuto del magazzino scalarli.</p>
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-3">
            {shown.map((l, i) => <LineRow key={i} l={l} i={i} mats={mats} onChange={(p) => upd(i, p)} />)}
            {!shown.length && <p className="text-sm text-muted-foreground">Nessun grafico con metri da scaricare.</p>}
          </div>
        )}
        <DialogFooter>
          <Button onClick={() => go.mutate()} disabled={!shown.length || missing || go.isPending} data-testid="discharge-confirm-btn">
            {go.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PackageMinus className="mr-2 h-4 w-4" />}Scarica dal magazzino
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
